"use client";

import { useState, useTransition } from "react";
import { usePathname } from "next/navigation";
import { useData, useLang } from "@/i18n/LangContext";
import type { CommentView } from "@/lib/comments";
import { signInWithApple, signInWithGoogle, signOutAction, submitComment } from "./actions";

interface CommentsSectionProps {
  comments: CommentView[];
  contentType: "WORK" | "BLOG";
  slug: string;
  pageTitle: string;
  viewer: { name: string | null; image: string | null } | null;
  providers: { google: boolean; apple: boolean };
}

type Feedback = { kind: "pending" | "error"; text: string } | null;

export function CommentsSection({ comments, contentType, slug, pageTitle, viewer, providers }: CommentsSectionProps) {
  const { ui } = useData();
  const lang = useLang();
  const pathname = usePathname() || "/";
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [isPending, startTransition] = useTransition();
  const t = ui.comments;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(ui.dateLocale, { day: "numeric", month: "long", year: "numeric" });

  const errorText = (code: string) =>
    code === "rate_limited" ? t.errorRateLimited : code === "invalid" ? t.errorInvalid : t.errorGeneric;

  const handleSubmit = (form: HTMLFormElement) => {
    const formData = new FormData(form);
    startTransition(async () => {
      const result = await submitComment(formData);
      if (result.ok) {
        form.reset();
        setReplyTo(null);
        setFeedback({ kind: "pending", text: t.pendingNotice });
      } else {
        setFeedback({ kind: "error", text: errorText(result.code) });
      }
    });
  };

  const renderForm = (parentId: string | null) => (
    <form
      className="comments--form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(e.currentTarget);
      }}
    >
      <input type="hidden" name="contentType" value={contentType} />
      <input type="hidden" name="slug" value={slug} />
      <input type="hidden" name="lang" value={lang} />
      <input type="hidden" name="pageTitle" value={pageTitle} />
      <input type="hidden" name="pagePath" value={pathname} />
      {parentId && <input type="hidden" name="parentId" value={parentId} />}
      <textarea
        name="body"
        required
        minLength={2}
        maxLength={2000}
        rows={parentId ? 3 : 4}
        placeholder={t.placeholder}
        disabled={isPending}
      />
      <div className="comments--form-row">
        <button type="submit" className="comments--submit no-select" disabled={isPending}>
          {isPending ? t.sending : t.submit}
        </button>
        {parentId && (
          <button type="button" className="comments--cancel no-select" onClick={() => setReplyTo(null)}>
            {t.cancel}
          </button>
        )}
      </div>
    </form>
  );

  const renderComment = (comment: CommentView) => (
    <div key={comment.id} className={`comments--item depth-${comment.depth}`} data-cy="comment">
      <div className="comments--head">
        {comment.authorImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={comment.authorImage} alt="" referrerPolicy="no-referrer" className="comments--avatar" />
        ) : (
          <span className="comments--avatar comments--avatar-fallback" aria-hidden="true">
            {comment.authorName.slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="comments--author">{comment.authorName}</span>
        <time className="comments--date" dateTime={comment.createdAt}>{formatDate(comment.createdAt)}</time>
        {comment.status === "PENDING" && <span className="comments--pending">{t.pendingBadge}</span>}
      </div>
      <p className="comments--body">{comment.body}</p>
      {viewer && comment.status === "VISIBLE" && comment.depth < 2 && (
        <button type="button" className="comments--reply no-select" onClick={() => { setFeedback(null); setReplyTo(comment.id); }}>
          {t.reply}
        </button>
      )}
      {replyTo === comment.id && renderForm(comment.id)}
      {byParent.get(comment.id)?.map(renderComment)}
    </div>
  );

  const byParent = new Map<string | null, CommentView[]>();
  for (const comment of comments) {
    const list = byParent.get(comment.parentId) ?? [];
    list.push(comment);
    byParent.set(comment.parentId, list);
  }
  const topLevel = byParent.get(null) ?? [];

  return (
    <section className="comments w-full max-w-3xl" data-cy="comments">
      <h2 className="comments--title">{t.title}</h2>

      {topLevel.length === 0 && <p className="comments--empty">{t.empty}</p>}
      <div className="comments--list">{topLevel.map(renderComment)}</div>

      {feedback && (
        <p className={feedback.kind === "pending" ? "comments--notice" : "comments--error"} role="status">
          {feedback.text}
        </p>
      )}

      {viewer ? (
        <div className="comments--composer">
          <div className="comments--signedin">
            <span>{t.signedInAs} <strong>{viewer.name ?? "…"}</strong></span>
            <button
              type="button"
              className="comments--signout no-select"
              onClick={() => startTransition(() => signOutAction(pathname))}
            >
              {t.signOut}
            </button>
          </div>
          {renderForm(null)}
        </div>
      ) : (providers.google || providers.apple) && (
        <div className="comments--login">
          <p>{t.loginPrompt}</p>
          <div className="comments--login-row">
            {providers.google && (
              <button
                type="button"
                className="comments--login-btn no-select"
                onClick={() => startTransition(() => signInWithGoogle(pathname))}
              >
                {t.loginGoogle}
              </button>
            )}
            {providers.apple && (
              <button
                type="button"
                className="comments--login-btn no-select"
                onClick={() => startTransition(() => signInWithApple(pathname))}
              >
                {t.loginApple}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
